/**
 * ONE requestAnimationFrame FOR THE WHOLE CHART.
 *
 * WHY THIS EXISTS
 * The chart ran five-plus independent rAF loops: FPCC's coalesced draw, the
 * hover coalescer, the wheel Animator, the x and y glides, the pinch and
 * footer coalescers, plus one inside LiquidityHeatLayer and another inside
 * OrderflowTrail. A profile put `requestAnimationFrame` at 506ms of SELF
 * time — 12.6% of the trace — before any of those callbacks did a thing.
 * That is registration and dispatch overhead, and it scales with the number
 * of loops, not the work.
 *
 * Multiplexing them onto one real rAF fixes the overhead. It also fixes a
 * whole class of bug that has nothing to do with performance:
 *
 *   SHARED TIMESTAMP. Every callback in a frame is handed the SAME ts, so
 *   two consumers cannot disagree about what time it is. Independent loops
 *   each got their own, which is how the heat layer and the trail ended up
 *   sampling an ease at a different phase than the chart drew it.
 *
 *   DETERMINISTIC ORDER. Priorities run input → animation → draw → layers,
 *   so geometry refs are written before the chart draws them, and the layers
 *   paint from state the chart has already rendered. Independent loops made
 *   that ordering luck. It is the structural version of the fix that had to
 *   be applied by hand to the wheel Animator's paint gate.
 *
 * SEMANTICS ARE requestAnimationFrame's, EXACTLY
 * One-shot. Returns an id. Cancelable. A callback scheduled from INSIDE a
 * tick runs on the NEXT frame, never the current one — same as rAF, and the
 * reason self-rescheduling loops keep working unchanged. Call sites convert
 * by substitution:
 *
 *   requestAnimationFrame(fn)  →  requestFrame(fn, FRAME_LAYER)
 *   cancelAnimationFrame(id)   →  cancelFrame(id)
 *
 * The driver idles when nothing is queued: no rAF is held open, so an
 * inactive chart costs nothing.
 */

// Lower runs earlier. Gaps left between them so a consumer can slot in
// without renumbering.
//
// INPUT   coalesced pointer state — must land before the draw reads it
// ANIM    animators and glides — WRITE the geometry refs
// DRAW    the chart's own paint — READS those refs
// LAYER   heat, trail, subgraphs — read the geometry the draw just used
export const FRAME_INPUT = 0
export const FRAME_ANIM = 10
export const FRAME_DRAW = 20
export const FRAME_LAYER = 30

// CALLED THROUGH A WRAPPER, NOT ALIASED. `const r = requestAnimationFrame`
// detaches the method from window, and modules are strict so `this` is
// undefined at the call — several Web APIs throw "Illegal invocation" on
// exactly that. Not worth finding out which engines care.
const _hasRaf = typeof requestAnimationFrame === 'function'
const _perf = (typeof performance !== 'undefined' && typeof performance.now === 'function')
  ? performance : null
const _hi = _perf ? () => _perf.now() : () => Date.now()
// Wall-clock instant the high-res clock started. rAF's ts is measured from
// the same origin, so _timeOrigin + ts is the Date.now()-comparable moment
// the frame will be PRESENTED. Feed timestamps are client Date.now(), which
// is why the bridge is needed at all rather than just handing out ts.
const _timeOrigin = (_perf && typeof _perf.timeOrigin === 'number')
  ? _perf.timeOrigin
  : (Date.now() - _hi())
// trail-reconnect-20261017: wall-clock offset for the high-res clock,
// re-checked every tick (see tick). Seeded from the original origin.
let _wallOff = _timeOrigin
const _raf = _hasRaf
  ? (fn) => requestAnimationFrame(fn)
  // Fallback must hand out a HIGH-RES-relative stamp, not a Date.now() one,
  // or _timeOrigin + ts double-counts the epoch and lands ~56 years out.
  : (fn) => setTimeout(() => fn(_hi()), 16)
const _caf = _hasRaf
  ? (id) => cancelAnimationFrame(id)
  : (id) => clearTimeout(id)

// BYPASS. localStorage.cheddarFrameDriver === '0' sends every call straight
// to the real rAF, ignoring priority — the pre-driver behaviour, one loop per
// caller. There to A/B the driver against a regression in a single line
// instead of reverting a multi-file change.
let _bypass = false
try { _bypass = localStorage.cheddarFrameDriver === '0' } catch (_) {}

// Counters for the same question. window.__cheddarFrames reports real rAF
// registrations vs ticks vs callbacks: rafs and ticks should track 1:1 and
// both should sit at the display rate. Two rafs per tick means something is
// double-arming; callbacks far above the loop count means a consumer is
// rescheduling more than once a frame.
const stats = { rafs: 0, ticks: 0, callbacks: 0, maxBatch: 0 }
if (typeof window !== 'undefined') window.__cheddarFrames = stats

// Hoisted: one function for the process, not one per frame.
const _byPrio = (a, b) => (a.prio - b.prio) || (a.id - b.id)

let queue = []
const byId = new Map()
let rafId = 0
let seq = 1
// Entries in `queue` whose cancelled flag is set. Lets cancelFrame release an
// idle frame without scanning the array — see the note there.
let cancelled = 0

function tick(ts) {
  rafId = 0
  stats.ticks++
  // trail-reconnect-20261017: re-anchor the high-res clock to Date.now().
  // timeOrigin + ts drifts from the wall clock (laptop sleep / long session),
  // while feed stamps (arrivalTs) and the out-of-tick fallback below use
  // Date.now(). With a drift, in-tick paints and sync paints placed the trail
  // at two different "now"s and it jumped back and forth (the stutter after a
  // sleep/reconnect). Re-anchor only past 4ms so normal frames keep sub-ms.
  {
    const off = Date.now() - _hi()
    if (!(Math.abs(off - _wallOff) <= 4)) _wallOff = off
  }
  _frameNow = _wallOff + ts
  // Swap the queue out BEFORE running anything. Callbacks that schedule
  // during a tick must land on the next frame, not extend this one — that
  // is rAF's contract, and a self-rescheduling loop would otherwise spin
  // forever inside a single frame.
  const batch = queue
  queue = []
  cancelled = 0
  // prio first, then insertion order within a prio. Array sort is stable in
  // modern engines, but the id tiebreak makes FIFO explicit rather than
  // implied.
  //
  // Comparator is hoisted (see _byPrio) and the sort is skipped when it
  // cannot reorder anything. An inline arrow allocated a fresh closure every
  // frame, and sort() ran on batches of 0 and 1 where the result is the input.
  // Individually trivial; 60x a second, forever, for no possible effect.
  if (batch.length > 1) batch.sort(_byPrio)
  if (batch.length > stats.maxBatch) stats.maxBatch = batch.length
  try {
  for (let i = 0; i < batch.length; i++) {
    const e = batch[i]
    byId.delete(e.id)
    if (e.cancelled) continue
    stats.callbacks++
    try {
      e.fn(ts)
    } catch (err) {
      // One broken subscriber must not take the frame down with it — the
      // whole point of sharing a loop is that consumers are isolated from
      // each other. Rethrow out of band so it still reaches the console and
      // any error reporting instead of being swallowed.
      setTimeout(() => { throw err }, 0)
    }
  }
  } finally {
    // Outside a tick frameNow() must fall back, so this cannot be left set.
    _frameNow = 0
  }
  // `&& !rafId` IS NOT OPTIONAL — WITHOUT IT THIS DOUBLES EVERY FRAME.
  // rafId was cleared at the top so callbacks can arm, and any callback that
  // reschedules (the heat loop, the animation pump, the glides) DOES arm via
  // requestFrame. Re-arming unconditionally here then overwrote rafId and
  // orphaned that first registration — still scheduled, no longer tracked. So
  // one frame with a self-rescheduling loop left TWO live rAF callbacks, each
  // of which did the same next frame. Exponential, and it locked the tab up
  // within seconds of load.
  //
  // The first test missed it because it counted callback invocations, which
  // stay correct the whole time: the extra ticks find an empty queue and do
  // nothing visible. Count real rAF registrations, not callbacks.
  if (queue.length && !rafId) { stats.rafs++; rafId = _raf(tick) }
}

/**
 * Schedule fn for the next frame.
 * @param {(ts: number) => void} fn
 * @param {number} prio one of FRAME_INPUT / FRAME_ANIM / FRAME_DRAW / FRAME_LAYER
 * @returns {number} id for cancelFrame
 */
export function requestFrame(fn, prio = FRAME_DRAW) {
  if (_bypass) { stats.rafs++; return _raf(fn) }
  const e = { id: seq++, fn, prio, cancelled: false }
  queue.push(e)
  byId.set(e.id, e)
  if (!rafId) { stats.rafs++; rafId = _raf(tick) }
  return e.id
}

/**
 * Cancel a pending callback. Safe to call with 0, null, or an id that has
 * already run — matching cancelAnimationFrame, so call sites do not need
 * their own guard.
 */
export function cancelFrame(id) {
  if (!id) return
  if (_bypass) { _caf(id); return }
  const e = byId.get(id)
  if (!e) return
  e.cancelled = true
  byId.delete(id)
  // NO SPLICE. This did queue.indexOf(e) then queue.splice(i, 1) — a linear
  // scan plus an array shift — to remove an entry that tick() already skips
  // via the `cancelled` flag it just set. The only thing the splice bought
  // was letting the queue reach length 0 so the rAF could be released early.
  //
  // A counter gives that without touching the array. When every queued entry
  // is cancelled there is nothing for the next tick to do, so release the
  // frame; tick resets the count when it swaps the queue out.
  cancelled++
  if (cancelled >= queue.length && rafId) { _caf(rafId); rafId = 0 }
}

let _frameNow = 0

/**
 * ONE CLOCK FOR EVERY TIME-DRIVEN LAYER.
 *
 * Returns the Date.now()-comparable instant of the frame currently being
 * dispatched. Every callback in a frame gets the SAME value, so two layers
 * that animate on time cannot drift against each other — which they did:
 * LiquidityHeatLayer and OrderflowTrail each called Date.now() inside their
 * own paint, so the ribbon and the trail were positioned from two different
 * "now"s taken however far apart the batch put them. The gap grows under
 * load, which is exactly when it shows.
 *
 * Three properties beyond that, all free:
 *
 *   SUB-MILLISECOND. Date.now() quantises to 1ms — 0.027px of ribbon motion
 *   at w/30000 px per ms. Small, but it is the same class of quantisation as
 *   the device-grid snap, and there is no reason to keep it.
 *
 *   MONOTONIC. Date.now() steps backward on an NTP correction. `now - t`
 *   then goes negative and the ribbon jumps. The high-res clock cannot.
 *
 *   PRESENTATION TIME. ts is when the frame will be SHOWN, not when this
 *   callback happened to run, so a layer delayed by whatever ran before it
 *   in the batch still positions itself correctly.
 *
 * OUTSIDE A TICK it falls back to Date.now() — a synchronous draw (FPCC's
 * useLayoutEffect path) is not in a batch and still needs an answer. Those
 * differ by under a frame. Same in bypass mode, where tick never runs and
 * every caller falls back, which keeps the bypass a true A/B.
 *
 * Does NOT address client/server clock skew. That is unchanged.
 */
export function frameNow() {
  return _frameNow || Date.now()
}

/** Pending callbacks this frame. Debugging only. */
export function pendingFrames() {
  return queue.length
}